/*
 * @Author: Ryan
 * @Date: 2025-12-03
 * @Description: Real use cases section with interactive tabs and carousel
 */
import React, { useCallback, useRef, useState } from 'react';

import MuteToggle from './MuteToggle';
import StartButton from './StartButton';

interface UseCase {
  id: string;
  tabTitle: string;
  contentTitle: string;
  description: string;
  imageUrl: string;
  videoUrl: string;
}

const UseCasesSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const carouselRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const useCases: UseCase[] = [
    {
      id: 'education',
      tabTitle: 'Online Education & Training',
      contentTitle: 'Online Education & Training',
      description:
        'Teachers and trainers can use FalcoCut to translate lectures, tutorials, and training materials into 35+ languages. Add subtitles, AI dubbing, or voice cloning to make lessons accessible to international learners, helping schools and companies reach a truly global classroom.',
      imageUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/image_1781066933388218368.jpeg',
      videoUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/videotranslationoccasiononlineeducationtraining_1781066928816431104.mp4',
    },
    {
      id: 'social-media',
      tabTitle: 'Social Media & Content Creators',
      contentTitle: 'Social Media & Content Creators',
      description:
        ' YouTube, TikTok, and Instagram creators can instantly localize their videos to connect with worldwide audiences. With FalcoCut’s AI video translator, you can add subtitles, voiceovers, and lip sync in multiple languages, boosting engagement, watch time, and subscriptions across platforms.',
      imageUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/image_1781066739036758016.jpeg',
      videoUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/videotranslationoccasionsocialmediacontentcreators_1781066734590795776.mp4',
    },
    {
      id: 'business',
      tabTitle: 'Business & Marketing Campaigns',
      contentTitle: 'Business & Marketing Campaigns',
      description:
        'Companies can translate product demos, ads, and promotional videos for different regions. FalcoCut helps brands build trust and expand into new markets with multilingual content that feels natural and localized—without the high costs of traditional dubbing or translation agencies.',
      imageUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/image_1781065455718141952.jpeg',
      videoUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/videotranslationoccasionbusinessmarketingcampaigns_1781065451276374016.mp4',
    },
    {
      id: 'events',
      tabTitle: 'Events, Conferences & Collaboration',
      contentTitle: 'Events, Conferences & Collaboration',
      description:
        'FalcoCut makes recorded conferences, webinars, and event sessions accessible to attendees everywhere. Translate video audio and add multilingual subtitles or dubbing so international teams and audiences can collaborate seamlessly and access content in their own language.',
      imageUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/image_1781066142946467840.jpeg',
      videoUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/videotranslationoccasioneventsconferencescollaboration_1781066138106241024.mp4',
    },
    {
      id: 'podcasts',
      tabTitle: 'Podcasts, Interviews & Media Production',
      contentTitle: 'Podcasts, Interviews & Media Production',
      description:
        'Podcasters and media producers can use FalcoCut to translate interviews, episodes, and visual podcasts into multiple languages. With voice cloning and accurate lip sync, speakers sound natural, helping content resonate with diverse audiences while preserving the original style.',
      imageUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/image_1781066341521592320.jpeg',
      videoUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/1765192904162_1781066336656199680.mp4',
    },
  ];

  // 处理 tab 点击
  const handleTabClick = (index: number) => {
    setActiveTab(index);
    // 移动端：滚动到对应的项
    if (carouselRef.current) {
      const itemWidth = carouselRef.current.scrollWidth / useCases.length;
      carouselRef.current.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth',
      });
    }
  };

  // 处理移动端滚动
  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const itemWidth = carouselRef.current.scrollWidth / useCases.length;
      const currentIndex = Math.round(scrollLeft / itemWidth);
      if (currentIndex !== activeTab) {
        setActiveTab(currentIndex);
      }
    }
  };

  // 处理视频播放点击
  const handlePlayClick = useCallback(
    (id: string) => {
      if (playingId === id) {
        // 如果点击的是当前播放的视频，则停止播放
        const video = videoRefs.current.get(id);
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
        setPlayingId(null);
      } else {
        // 停止之前播放的视频
        if (playingId) {
          const prevVideo = videoRefs.current.get(playingId);
          if (prevVideo) {
            prevVideo.pause();
            prevVideo.currentTime = 0;
          }
        }
        // 播放新视频
        const video = videoRefs.current.get(id);
        if (video) {
          video.play().catch((error) => {
            console.warn('视频播放失败:', error);
          });
        }
        setPlayingId(id);
      }
    },
    [playingId],
  );

  // 处理视频容器点击 - 点击视频任意处暂停/播放
  const handleVideoClick = useCallback(
    (id: string, event: React.MouseEvent) => {
      // 阻止事件冒泡
      event.stopPropagation();

      const video = videoRefs.current.get(id);
      if (!video) return;

      if (playingId === id) {
        // 当前视频正在播放，暂停
        video.pause();
        setPlayingId(null);
      } else {
        // 停止之前播放的视频
        if (playingId) {
          const prevVideo = videoRefs.current.get(playingId);
          if (prevVideo) {
            prevVideo.pause();
            prevVideo.currentTime = 0;
          }
        }
        // 播放当前视频
        video.play().catch((error) => {
          console.warn('视频播放失败:', error);
        });
        setPlayingId(id);
      }
    },
    [playingId],
  );

  // 切换静音状态
  const handleMuteToggle = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  // 更新所有视频的静音状态
  React.useEffect(() => {
    videoRefs.current.forEach((video) => {
      video.muted = isMuted;
    });
  }, [isMuted]);

  return (
    <section className="w-full bg-[#1A1B25] py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto ">
        {/* 标题区域 */}
        <div className="text-center mb-8 pad:mb-12">
          <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-white mb-3 pad:mb-4">
            Video Translation for Diverse Real-World Occasions
          </h2>
          <p className="text-sm pad:text-base text-white max-w-4xl mx-auto mb-6 pad:mb-8">
            FalcoCut helps educators, creators, businesses, and media teams translate
            videos into 35 languages with AI voices, subtitles, and lip sync. From online
            learning to global marketing, our AI video translator makes it easy to
            localize content, connect with diverse audiences, and scale your message
            worldwide.
          </p>
          <StartButton />
        </div>

        {/* Tab 导航 - 桌面端可见 */}
        <div className="hidden sm:grid grid-cols-5 gap-2 mb-10">
          {useCases.map((useCase, index) => (
            <button
              key={useCase.id}
              type="button"
              onClick={() => handleTabClick(index)}
              className={`
                px-6 py-3 rounded-lg font-medium text-sm 
                transition-all duration-300
                ${
                  activeTab === index
                    ? 'bg-[#3366FF] text-white'
                    : 'bg-[#36394A] text-gray-300 hover:bg-[#3366FF]/20'
                }
              `}
            >
              {useCase.tabTitle}
            </button>
          ))}
        </div>

        {/* 内容区域 - 桌面端 */}
        <div className="hidden sm:block">
          {useCases.map((useCase, index) => (
            <div
              key={useCase.id}
              className={`
                grid grid-cols-2 gap-8 pad:gap-10 sm:gap-12
                transition-opacity duration-500
                ${activeTab === index ? 'opacity-100' : 'opacity-0 hidden'}
              `}
            >
              {/* 左侧文字 */}
              <div className="flex flex-col justify-center">
                <h3 className="text-xl pad:text-2xl font-bold text-white mb-4">
                  {useCase.contentTitle}
                </h3>
                <p className="text-sm pad:text-base sm:text-lg text-gray-300 leading-relaxed">
                  {useCase.description}
                </p>
              </div>

              {/* 右侧视频 */}
              <div className="relative rounded-2xl overflow-hidden bg-[#36394A]">
                <div
                  className="aspect-video relative cursor-pointer"
                  onClick={(e) => handleVideoClick(useCase.id, e)}
                >
                  {/* 视频元素 - 使用 poster 作为封面，防止闪动 */}
                  <video
                    ref={(el) => {
                      if (el) {
                        videoRefs.current.set(useCase.id, el);
                      }
                    }}
                    className="w-full h-auto object-cover"
                    src={useCase.videoUrl}
                    poster={useCase.imageUrl}
                    playsInline
                    loop
                    muted={isMuted}
                    preload="metadata"
                  />
                  {/* 播放按钮覆盖层 - 仅在未播放时显示 */}
                  {playingId !== useCase.id && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-white ml-1"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  )}
                  {/* 静音切换按钮 - 仅在播放时显示 */}
                  {playingId === useCase.id && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <MuteToggle isMuted={isMuted} onToggle={handleMuteToggle} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 内容区域 - 移动端轮播 */}
        <div className="sm:hidden">
          {/* Tab 标签 - 移动端底部显示 */}
          <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar pb-2">
            {useCases.map((useCase, index) => (
              <button
                key={useCase.id}
                type="button"
                onClick={() => handleTabClick(index)}
                className={`
                  px-4 py-2 rounded-lg font-medium text-xs whitespace-nowrap
                  transition-all duration-300
                  ${
                    activeTab === index
                      ? 'bg-[#3366FF] text-white'
                      : 'bg-[#36394A] text-gray-300'
                  }
                `}
              >
                {useCase.tabTitle}
              </button>
            ))}
          </div>
          {/* 轮播容器 */}
          <div
            ref={carouselRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {useCases.map((useCase) => (
              <div key={useCase.id} className="w-full flex-shrink-0 snap-center px-2">
                <div className="flex flex-col gap-6">
                  {/* 视频在上 */}
                  <div className="relative rounded-2xl overflow-hidden bg-[#36394A]">
                    <div
                      className="aspect-video relative cursor-pointer"
                      onClick={(e) => handleVideoClick(`${useCase.id}-mobile`, e)}
                    >
                      {/* 视频元素 - 使用 poster 作为封面，防止闪动 */}
                      <video
                        ref={(el) => {
                          if (el) {
                            videoRefs.current.set(`${useCase.id}-mobile`, el);
                          }
                        }}
                        className="w-full h-auto object-cover"
                        src={useCase.videoUrl}
                        poster={useCase.imageUrl}
                        playsInline
                        loop
                        muted={isMuted}
                        preload="metadata"
                      />
                      {/* 播放按钮 - 仅在未播放时显示 */}
                      {playingId !== `${useCase.id}-mobile` && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                            <svg
                              className="w-6 h-6 text-white ml-1"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      )}
                      {/* 静音切换按钮 - 仅在播放时显示 */}
                      {playingId === `${useCase.id}-mobile` && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <MuteToggle isMuted={isMuted} onToggle={handleMuteToggle} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 文字在下 */}
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      {useCase.contentTitle}
                    </h3>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      {useCase.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
